import { useQuery, useMutation, useQueryClient, UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import type {
  AvailableStepsResponse,
  CreateSimulationBody,
  SimulationConfig,
  SimulationResult,
  SimulationsListResponseNormalized,
  UpdateSimulationBody,
} from "../../services/simulations.service";
import { simulationsService } from "../../services/simulations.service";

export const simulationKeys = {
  all: ["simulations"] as const,
  list: () => [...simulationKeys.all, "list"] as const,
  details: () => [...simulationKeys.all, "detail"] as const,
  detail: (id: number) => [...simulationKeys.details(), id] as const,
  results: () => [...simulationKeys.all, "result"] as const,
  result: (id: number) => [...simulationKeys.results(), id] as const,
  availableSteps: () => [...simulationKeys.all, "available_steps"] as const,
};

export function useSimulationsList(
  options?: Omit<UseQueryOptions<SimulationsListResponseNormalized, Error>, "queryKey" | "queryFn">
) {
  return useQuery<SimulationsListResponseNormalized, Error>({
    queryKey: simulationKeys.list(),
    queryFn: () => simulationsService.getList(),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useSimulationDetail(
  id: number,
  options?: Omit<UseQueryOptions<SimulationConfig, Error>, "queryKey" | "queryFn">
) {
  return useQuery<SimulationConfig, Error>({
    queryKey: simulationKeys.detail(id),
    queryFn: () => simulationsService.getById(id),
    enabled: id > 0,
    staleTime: 0,
    ...options,
  });
}

export function useAvailablePipelineSteps(
  options?: Omit<UseQueryOptions<AvailableStepsResponse, Error>, "queryKey" | "queryFn">
) {
  return useQuery<AvailableStepsResponse, Error>({
    queryKey: simulationKeys.availableSteps(),
    queryFn: () => simulationsService.getAvailablePipelineSteps(),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateSimulation(
  options?: UseMutationOptions<SimulationConfig, Error, CreateSimulationBody>
) {
  const queryClient = useQueryClient();
  return useMutation<SimulationConfig, Error, CreateSimulationBody>({
    mutationFn: (body) => simulationsService.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.list() });
    },
    ...options,
  });
}

export function useUpdateSimulation(
  options?: UseMutationOptions<SimulationConfig, Error, { id: number; data: UpdateSimulationBody }>
) {
  const queryClient = useQueryClient();
  return useMutation<SimulationConfig, Error, { id: number; data: UpdateSimulationBody }>({
    mutationFn: ({ id, data }) => simulationsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.list() });
    },
    ...options,
  });
}

export function useDeleteSimulation(
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => simulationsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.list() });
    },
    ...options,
  });
}

export function useSimulationResult(
  id: number,
  options?: Omit<UseQueryOptions<SimulationResult, Error>, "queryKey" | "queryFn">
) {
  return useQuery<SimulationResult, Error>({
    queryKey: simulationKeys.result(id),
    queryFn: () => simulationsService.getResult(id),
    enabled: id > 0,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useSetCurrentSimulation(
  options?: UseMutationOptions<SimulationConfig, Error, number>
) {
  const queryClient = useQueryClient();
  return useMutation<SimulationConfig, Error, number>({
    mutationFn: (id) => simulationsService.setCurrent(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.list() });
      queryClient.invalidateQueries({ queryKey: simulationKeys.detail(id) });
    },
    ...options,
  });
}

